import { apiGet, apiPatch, apiPost, apiUrl, prettifyApiError } from "@/shared/api";
import { downloadFile } from "@/shared/download";
import type {
  BatchStatus,
  CampoData,
  CreateDocumentsResult,
  EditBody,
  EsitoMetroUpload,
  ManualBody,
  MarkBody,
  Proposal,
  SortField,
  SyncNowResult,
  WorklistResponse,
} from "./types";

/** Filtri della vista corrente, condivisi da worklist ed export XLSX: un solo
 *  tipo così le due chiamate non possono divergere (l'export deve esportare
 *  ESATTAMENTE quello che si sta guardando). */
export interface WorklistFilters {
  status?: string;
  kind?: string;
  channel?: string;
  doc_type?: string;
  q?: string;
  sort?: SortField;
  direction?: "asc" | "desc";
  /** Periodo su `date_field`, estremi INCLUSI (ISO YYYY-MM-DD). */
  from_date?: string;
  to_date?: string;
  /** Su quale data lavora il periodo. Default backend: `order`. */
  date_field?: CampoData;
  limit?: number;
  offset?: number;
}

export async function fetchWorklist(
  params: WorklistFilters,
  signal?: AbortSignal,
): Promise<WorklistResponse> {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== "") qs.set(k, String(v));
  }
  return apiGet(`/api/invoicing/worklist?${qs.toString()}`, signal ? { signal } : undefined);
}

export function markProposal(id: number, body: MarkBody): Promise<Proposal> {
  return apiPost(`/api/invoicing/proposals/${id}/mark`, body);
}

export function excludeProposal(id: number, reason: string): Promise<Proposal> {
  return apiPost(`/api/invoicing/proposals/${id}/exclude`, { reason });
}

export function reopenProposal(id: number): Promise<Proposal> {
  return apiPost(`/api/invoicing/proposals/${id}/reopen`);
}

export function reclassifyProposal(id: number): Promise<Proposal> {
  return apiPost(`/api/invoicing/proposals/${id}/reclassify`);
}

export function createManual(body: ManualBody): Promise<Proposal> {
  return apiPost(`/api/invoicing/manual`, body);
}

export function confirmNc(
  id: number,
  body: { refund_amount: number; refund_date: string },
): Promise<Proposal> {
  return apiPost(`/api/invoicing/nc/${id}/confirm`, body);
}

/** Apre a mano la proposta di storno di un documento già emesso.
 *
 *  Copre TUTTI i canali, anche quelli che i rimborsi non li espongono
 *  (ManoMano, BricoBravo, Amazon via Poleepo). La NC nasce in revisione e
 *  senza importo: lo mette Davide dal dialog «Conferma rimborso», dopo averlo
 *  verificato sull'ordine del marketplace. */
export function createStorno(id: number): Promise<Proposal> {
  return apiPost(`/api/invoicing/nc/${id}/create-storno`);
}

/** Sincronizzazione forzata: detect+NC+enrich+backfill+riconciliazione
 *  subito (senza aspettare il giro schedulato). */
export function syncNow(): Promise<SyncNowResult> {
  return apiPost(`/api/invoicing/sync-now`);
}

/** Modifica campi di una proposta SENZA documento emesso (round 3 punto 6). */
export function editProposal(id: number, body: EditBody): Promise<Proposal> {
  return apiPatch(`/api/invoicing/proposals/${id}`, body);
}

/** Parcheggia manualmente una proposta in «in attesa» (spedizione/pagamento). */
export function setPending(id: number, which: "spedizione" | "pagamento"): Promise<Proposal> {
  return apiPost(`/api/invoicing/proposals/${id}/set-pending`, { which });
}

/** Forza una proposta «in attesa» a da_fatturare (riclassifica la sigla). */
export function markReady(id: number): Promise<Proposal> {
  return apiPost(`/api/invoicing/proposals/${id}/mark-ready`);
}

/**
 * Segna come inviate allo SdI più righe in blocco (tab «Da inviare»).
 *
 * ponytail: nessun endpoint bulk nuovo — riusa il `mark` per riga che esiste
 * già, in SEQUENZA. Sono decine di righe, non migliaia, e ogni riga conserva il
 * suo evento in cronologia (un endpoint bulk lo perderebbe o andrebbe
 * riscritto). Sequenziale e non parallelo: sono scritture sulla stessa tabella
 * e l'ordine degli eventi resta leggibile.
 *
 * Perché è manuale: l'MCP Eureka NON espone alcun campo che distingua «creata»
 * da «trasmessa» — verificato il 21/07 su una FA che Davide aveva annotato come
 * NON trasmessa: `SW_STATO_FATT`, `DATA_FATTURA`, `NUMERO_FATTURA`, presenza in
 * contabilità F18 e link PDF risultavano identici a quelli di una trasmessa.
 * Dedurre l'invio da uno di quei campi marcherebbe «fatturata» una fattura mai
 * partita: meglio un click che una bugia.
 */
export async function markSentBulk(
  rows: Array<Pick<Proposal, "id" | "doc_type" | "document_number" | "document_date">>,
): Promise<{ inviate: number; errori: Array<{ id: number; errore: string }> }> {
  const errori: Array<{ id: number; errore: string }> = [];
  let inviate = 0;
  for (const r of rows) {
    if (!r.doc_type || !r.document_number || !r.document_date) {
      errori.push({ id: r.id, errore: "manca sigla, numero o data documento" });
      continue;
    }
    try {
      await markProposal(r.id, {
        doc_type: r.doc_type,
        document_number: r.document_number,
        document_date: r.document_date,
        status: "fatturata",
      });
      inviate += 1;
    } catch (err) {
      // NON ``err.message``: arriva da ``raiseApiError`` col prefisso
      // ``[/api/invoicing/proposals/<id>/mark]`` e, sui corpi non-JSON, con la
      // sola status line in inglese. Questa stringa finisce interpolata nel
      // riepilogo di «Segna inviate», cioè sotto gli occhi dell'operatore su una
      // schermata fiscale: va ripulita qui, dove l'errore viene raccolto.
      errori.push({ id: r.id, errore: prettifyApiError(err) });
    }
  }
  return { inviate, errori };
}

/**
 * ANTEPRIMA dei documenti da creare in Eureka. NON scrive nulla, mai.
 *
 * 🔴 Costruisce i payload veri e ritorna, riga per riga, cosa creerebbe e cosa
 * rifiuta (anti-doppione, quadratura righe ↔ importo, recapito SdI mancante).
 * È il passo obbligato prima della creazione: l'MCP Eureka non ha una delete,
 * un documento creato per sbaglio si corregge solo con uno storno.
 *
 * Niente parametro `dryRun`: da quando l'emissione vera vive in `avviaBatch`,
 * questo endpoint risponde 400 a `dry_run:false`. Un flag che ha un solo valore
 * lecito è solo un modo per sbagliare.
 */
export function anteprimaDocumenti(proposalIds: number[]): Promise<CreateDocumentsResult> {
  return apiPost(`/api/invoicing/documents/create`, {
    proposal_ids: proposalIds,
    dry_run: true,
  });
}

/**
 * Avvia l'emissione VERA e ritorna subito: il batch gira in background.
 *
 * 🔴 Da qui in poi si scrivono documenti fiscali veri. La risposta (202) è solo
 * il `batch_id`, perché ogni documento sono 2-4 chiamate MCP in sequenza e un
 * batch da 20 righe dura minuti: tenere aperta la connessione fino alla fine
 * significherebbe che un timeout della rete «annulla» un'emissione già avvenuta.
 *
 * 409 = c'è già un batch aperto (uno alla volta, per non incrociare numero e
 * data nella serie): il backend risponde con un messaggio già in italiano, che
 * `prettifyApiError` lascia passare intatto.
 */
export function avviaBatch(proposalIds: number[]): Promise<{ batch_id: string }> {
  return apiPost(`/api/invoicing/documents/batch`, { proposal_ids: proposalIds });
}

/**
 * Stato del batch riga per riga, sorgente del caricamento a schermo.
 *
 * `completo` aggiunge payload e risposta grezza di Eureka: sono grossi e non
 * servono a ogni giro di polling, si chiedono solo quando si apre «Dettagli
 * tecnici» su una riga andata male.
 */
export function fetchBatch(batchId: string, completo = false): Promise<BatchStatus> {
  return apiGet(`/api/invoicing/documents/batch/${batchId}${completo ? "?completo=true" : ""}`);
}

/** L'ultimo batch (o `null`): serve alla pagina per ritrovare un'emissione
 *  ancora in corso dopo che la finestra è stata chiusa o la pagina ricaricata. */
export function fetchUltimoBatch(signal?: AbortSignal): Promise<BatchStatus | null> {
  return apiGet(`/api/invoicing/documents/batch`, signal ? { signal } : undefined);
}

/**
 * Scarica un file del portale. Il corpo vive in ``shared/download.ts``: era
 * copiato qui, in ordini, in resi e in messaggi, e le copie divergevano.
 *
 * ``apiUrl`` e non il default ``apiAssetUrl``: in DEV il download deve passare
 * dal proxy Vite (stessa origin = cookie di sessione), non dal loopback
 * assoluto.
 */
function scaricaFile(path: string, nomeFallback: string): Promise<void> {
  return downloadFile(path, nomeFallback, apiUrl(path));
}

/** Scarica l'XLSX della vista corrente (stessi filtri della worklist). */
export function downloadExportXlsx(params: WorklistFilters): Promise<void> {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== "") qs.set(k, String(v));
  }
  return scaricaFile(`/api/invoicing/export?${qs.toString()}`, "fatturazione.xlsx");
}

/**
 * PDF della fattura di cortesia di un documento già emesso.
 *
 * La chiave è il TENTATIVO di emissione, non la proposta: il PDF si stampa
 * dalla risposta che Eureka ha dato a `documento_crea` e che vive nel diario
 * (`document_attempts.risposta`) — nessuna nuova chiamata al gestionale,
 * quindi nessun rischio sul refresh_token monouso. 404 = quel tentativo non ha
 * una risposta da cui stampare.
 */
export function scaricaPdfFattura(attemptId: number): Promise<void> {
  return scaricaFile(`/api/invoicing/documents/${attemptId}/pdf`, `fattura-${attemptId}.pdf`);
}

/**
 * Carica a mano la fattura sull'ordine Metro.
 *
 * ⚠️ `completato: false` **non è un errore**: l'endpoint di Metro è per RIGA
 * d'ordine e salta le righe non ancora `shipped`, e le fatture si emettono
 * quasi sempre prima che Metro spedisca. Vuol dire «riprova più tardi», e la
 * ripassata schedulata ci riprova da sola. Chi consuma questa risposta non
 * deve mostrarla come fallimento — è lo stesso inganno del `created: 0`.
 */
export function caricaFatturaSuMetro(attemptId: number): Promise<EsitoMetroUpload> {
  return apiPost(`/api/invoicing/documents/${attemptId}/metro-upload`);
}
